/**
 * @returns boolean if device is iOS
 */
export const iOS = (): boolean => {
  return (
    [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod',
    ].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  )
}

/**
 * @returns return full height for 100dvh support for ios
 */
export const getFullHeight = () => {
  return iOS() ? '-webkit-fill-available !important;' : '100dvh !important'
}

export const capitalizeWord = (word: string) => {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

export const componentToHex = (c: number) => {
  const hex = c.toString(16)
  return hex.length === 1 ? `0${hex}` : hex
}

export const hexToRgba = (hex: string, opacity: number): string | null => {
  let hexCopy = hex
  let opacityCopy = opacity
  // Check if the input hex is a valid 3-digit or 6-digit hex color code
  const hexRegex = /^#[0-9a-f]{3,6}$/i
  if (!hexCopy.match(hexRegex)) {
    return null
  }

  // Remove the '#' symbol from the hex code
  hexCopy = hexCopy.replace('#', '')

  // Convert 3-digit hex to 6-digit hex if needed
  if (hexCopy.length === 3) {
    hexCopy = hexCopy
      .split('')
      .map((char) => char.repeat(2))
      .join('')
  }

  // Parse the hex values to integers
  const r = parseInt(hexCopy.slice(0, 2), 16)
  const g = parseInt(hexCopy.slice(2, 4), 16)
  const b = parseInt(hexCopy.slice(4, 6), 16)

  // Validate the opacity value (must be between 0 and 1)
  opacityCopy = Math.min(Math.max(opacityCopy, 0), 1)

  // Create the RGBA color string
  return `rgba(${r}, ${g}, ${b}, ${opacityCopy})`
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  let r: number
  let g: number
  let b: number

  if (s === 0) {
    // eslint-disable-next-line no-multi-assign
    r = g = b = l // Achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      // eslint-disable-next-line no-param-reassign
      if (t < 0) t += 1
      // eslint-disable-next-line no-param-reassign
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)]
}

type AlphaValue = 0 | 0.1 | 0.2 | 0.3 | 0.4 | 0.5 | 0.6 | 0.7 | 0.8 | 0.9 | 1

export function hslStringToRgba(hslStr: string, alpha: AlphaValue): string {
  const match = hslStr.match(/hsl\((\d+),\s*([\d.]+)%,\s*([\d.]+)%\)/)
  if (!match) throw new Error('Invalid HSL string')

  const h = parseInt(match[1], 10) / 360
  const s = parseInt(match[2], 10) / 100
  const l = parseInt(match[3], 10) / 100

  const [r, g, b] = hslToRgb(h, s, l)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const getEstDay = () => {
  const now = new Date()
  const estOffset = -5 * 60 // EST is UTC-5
  const nowUtcOffset = now.getTimezoneOffset()
  return new Date(now.getTime() + (nowUtcOffset + estOffset) * 60000)
}
